import { Outlet, useLocation, useParams } from 'react-router'
import DesktopSidebar from './sidebar/DesktopSidebar'
import { ChevronLeft } from '@/assets/icons'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { toggleSidebarVisibility } from '@/store/slices/ui-state.slice'
import StripePortalDialog from '@/account/components/StripePortalDialog'
import { isPaxtonBrand, useOverridePaxton } from '@/util/enterprise'
import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import ReferralDialog from '@/referral/views/referral-dialog'
import { useSettings } from '@/settings/hooks/useSettings'
import { MattersActions } from '@/matter/store/slice'
import ManageMembersDialogContainer from '@/matter/components/manage-members/ManageMembersDialogContainer'
import CreateOrEditMatterDialog from '@/matter/components/matter-dialogs/CreateOrEditMatterDialog'
import DeleteMatterDialog from '@/matter/components/matter-dialogs/DeleteMatterDialog'
import AcceptMatterInviteDialog from '@/matter/components/matter-dialogs/AcceptMatterInviteDialog'
import { kRouteSegments } from '@/routing/paths'
import AnonFeatureGuard from '@/components/guards/AnonFeatureGuard'
import NewChatMatterBoundButton from '@/chat-common/new_chat/components/NewChatMatterBoundButton'
import ChatListSidebar from '@/chat-common/conversation_list/components/ChatListSidebar'
import MatterBreadcrumbsChat from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsChat'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { canViewChats } from '@/matter/utils/matter-permissions'
import { useRestoreSessionMatter } from '@/matter/hooks/useRestoreSessionMatter'
import SubscriptionNoticeBanner from '@/account/components/subscription-notice-banner/SubscriptionNoticeBanner'
import DashboardMobileTopBar from './DashboardMobileTopBar'
import DashboardMobileChatHistory from './DashboardMobileChatHistory'
import MobileSidebar from './sidebar/MobileSidebar'
export default function DashboardLayout() {
  // Store - UI State
  const dispatch = useAppDispatch()
  const uiState = useAppSelector((state: RootState) => state.uiState)
  const hideSidebars = uiState.hideSidebars
  const isPaxtonHost = isPaxtonBrand()
  const isOverridePaxton = useOverridePaxton()
  const { getSettings } = useSettings()

  // Auth Context
  const authContext = useContext(AuthContext)
  const { user } = authContext
  const userIsAnon = user?.isAnonymous

  const { chatFeature, matterId: urlMatterId, chatId } = useParams()
  const validChatFeature: boolean = chatFeature === kRouteSegments.assistant

  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlMatterId ?? null }))

  // Assume chat access until matters load so we don't flash the chrome away from users during
  // initial load. Under collaboration, collaborators can view chat history and start their own
  // chats, while continuing someone else's chat is blocked inside AgentChatWindow.
  const canAccessChats = !mattersInitialLoadComplete || canViewChats(urlMatter)

  const shouldDisplayBreadcrumbsForFeature: boolean = !userIsAnon && validChatFeature && canAccessChats

  // Show only for valid chat features, and hide on the unassigned-matter empty state (no chatId after matters finish loading)
  const showChatListSidebar = validChatFeature && canAccessChats && (!!chatId || (mattersInitialLoadComplete && !isUnassignedMatter(urlMatter)))

  // Load settings and matters when dashboard mounts for authenticated users
  useEffect(() => {
    if (!userIsAnon) {
      getSettings()
      dispatch(MattersActions.refreshMatterList({ forceRefresh: false }))
    }
  }, [userIsAnon])

  // Restore the active matter from session storage
  useRestoreSessionMatter()

  const location = useLocation()
  const isMattersPage = location.pathname === '/dashboard/matters'
  useEffect(() => {
    // Preventing the active matter to stay set when navigating to the matters list page
    // The single source of truth for the active matter is the URL
    // So when the user navigates to the matters list page, the active matter needs to be cleared and set to null
    if (isMattersPage) {
      dispatch(MattersActions.setActiveMatterId({ matterId: null }))
    }
  }, [isMattersPage, dispatch])

  // Attach sidebar for Paxton or authed enterprise users
  const attachSidebar = isPaxtonHost || !userIsAnon || isOverridePaxton

  return (
    <main className={'bg-brand-neutral-gradient flex flex-col h-screen overflow-hidden'}>
      {/* Global Dialogs */}
      <StripePortalDialog />
      <ReferralDialog />
      <CreateOrEditMatterDialog />
      <DeleteMatterDialog />
      <AcceptMatterInviteDialog />
      <ManageMembersDialogContainer />

      {/* Subscription Notice Banner */}
      <SubscriptionNoticeBanner />

      {/* Mobile Only Components */}
      <div className="lg:hidden">
        {/* Mobile Top Bar */}
        <DashboardMobileTopBar />

        {/* Mobile Sidebar (slide-out) */}
        <MobileSidebar />

        {/* Mobile Chat History (slide-out) */}
        <AnonFeatureGuard showAuthOnGuard={false}>
          <DashboardMobileChatHistory />
        </AnonFeatureGuard>
      </div>

      <div className={'flex-1 flex overflow-hidden'}>
        {/* Desktop Sidebar */}
        {attachSidebar && (
          <div className={`hidden lg:flex h-full items-center`}>
            <DesktopSidebar hideDesktopSidebars={hideSidebars} />
          </div>
        )}

        {/* When sidebars are visible on large screens, account for the width of the sidebars */}
        <div className={`flex-1 flex flex-col w-full overflow-hidden ${hideSidebars ? '' : 'lg:w-[calc(100%-16rem)]'}`}>
          {/* Matter Breadcrumb Bar */}
          {shouldDisplayBreadcrumbsForFeature && <MatterBreadcrumbsChat />}

          {/* Chat List Sidebar and Main Content Container */}
          <div className={`flex min-h-0 flex-1 overflow-y-auto`}>
            {/* Desktop Chat List Sidebar - mobile version lives in DashboardMobileChatHistory in the mobile top bar */}
            {showChatListSidebar && (
              <div className="hidden lg:flex">
                <AnonFeatureGuard showAuthOnGuard={false}>
                  <div className={`transition-all ease-in-out duration-500 overflow-hidden ${hideSidebars ? 'w-0' : 'w-64 3xl:w-80'}`}>
                    <div className="flex flex-col h-full">
                      {/* Sticky section with new chat button */}
                      <div className="flex-shrink-0 pl-2 py-2 w-full">
                        <NewChatMatterBoundButton />
                      </div>
                      {/* Scrollable chat list */}
                      <div className="flex-1 overflow-y-auto">
                        <ChatListSidebar />
                      </div>
                    </div>
                  </div>
                </AnonFeatureGuard>
              </div>
            )}

            {/* Desktop Sidebar Collapse Button */}
            <div className="hidden lg:flex">
              <button
                onClick={() => dispatch(toggleSidebarVisibility())}
                className={`relative z-0 group h-full flex transition-all ease-in-out duration-200 items-center justify-center ${hideSidebars ? 'w-8' : 'w-6'}`}
              >
                <div className={'relative h-full flex items-center justify-center'}>
                  <div className={'absolute z-10 bg-brand-neutral-100 py-4 rounded-full'}>
                    <div
                      className={`group-hover:w-6 h-6 bg-brand-neutral-300 group-hover:bg-brand-neutral-400 transition-all ease-in-out duration-300 time rounded-full ${
                        hideSidebars ? 'w-6 rotate-180 duration-1000' : 'w-[3px]'
                      }`}
                    >
                      <ChevronLeft className={'w-full h-full text-white'} />
                    </div>
                  </div>
                  <div
                    className={'absolute h-2/3 group-hover:h-4/5 w-[1px] bg-brand-neutral-200 transition-all ease-in-out rounded-full duration-200 time'}
                  ></div>
                </div>
              </button>
            </div>

            {/* Main Content */}
            <Outlet />
          </div>
        </div>
      </div>
    </main>
  )
}
